import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { useAuthHydrated } from './hooks/useAuthHydrated';
import { useFeedFilter } from './stores/feedFilter';
import { syncUserFromServer } from './api/user';
import { initSentry, setSentryUser } from './utils/sentry';
import { initAnalytics } from './utils/analytics';
import { initLiveUpdates } from './utils/liveUpdate';
import { useAuthStore } from './stores/AuthState';
import ErrorBoundary from './components/ErrorBoundary';
import QaCrashTrigger from './components/QaCrashTrigger';
import './index.css';

// Initialise crash/error reporting as early as possible (no-op in local dev).
initSentry();

// Tell the OTA plugin this bundle booted successfully. MUST run synchronously
// and early — the plugin rolls this bundle back after 10s otherwise.
initLiveUpdates();

// Keep the Sentry user in sync with auth (id + username only — no PII).
const applyUser = (info: { id: number | null; username: string | null }) =>
  setSentryUser(
    info.id != null ? { id: String(info.id), username: info.username ?? undefined } : null,
  );
applyUser(useAuthStore.getState().userInfo);
useAuthStore.subscribe((s) => applyUser(s.userInfo));

// ─── Query client ────────────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// ─── Router ──────────────────────────────────────────────────────────────────
const router = createRouter({
  routeTree,
  context: {},
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

// Audience (Matomo, no cookies — web 1/2, native app 3/4 for PROD/QA, nothing locally): one page view at start, then one per path change.
void initAnalytics(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Gate the router on auth-store hydration. The persisted store reads from
 * Capacitor Preferences (async), so rendering before hydration would flash a
 * logged-out state on cold start. We render nothing (the static splash in
 * index.html stays visible) until the store is ready.
 */
function App() {
  const hydrated = useAuthHydrated();

  // feed-genre-filter — seed the style filter from the ACCOUNT once auth is
  // ready. Fire-and-forget on purpose: the local (device) state has already
  // painted a correctly filtered feed, so this only reconciles. Awaiting it
  // would reintroduce the flash the local cache exists to prevent.
  useEffect(() => {
    if (!hydrated) return;
    void useFeedFilter.getState().hydrateFromServer();
    // profile-sync-on-launch — same for the profile: the device copy paints,
    // the account reconciles (favourite streaming service, language…).
    void syncUserFromServer();
  }, [hydrated]);

  if (!hydrated) return null;
  return (
    <>
      <QaCrashTrigger />
      <RouterProvider router={router} />
    </>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
